import { useState, type CSSProperties, type FormEvent, type JSX } from 'react'
import logoUrl from '../assets/aps-logo.svg'
import { api, type User } from '../api.js'
import { T } from '../tokens.js'

/**
 * The sign-in screen, transcribed from the mock's `isSignin` branch.
 *
 * **Two of the mock's controls are deliberately not here.** `Keep me signed in`
 * is a checkbox bound to nothing and `Forgot password` is an `href="#"`, and
 * neither has a backend: sessions have a fixed seven-day TTL
 * (`routes/auth.ts`) and there is no password-reset flow anywhere in this
 * system. A control that does nothing is worse than an absent one, because a
 * rep will tick it and believe it — they will close their laptop expecting to
 * stay signed in, or spend an afternoon waiting for a reset email nothing
 * sends. `SignIn.test.tsx` asserts their absence so that "the mock has it" does
 * not quietly put them back.
 *
 * **There is no client-side email validation, and the form is `noValidate`.**
 * Not an oversight and not laziness. Anything the browser refuses to send is
 * something the server never gets to answer uniformly, and the whole
 * enumeration defence — `authenticate` burning a dummy scrypt hash on the
 * unknown-email path so that an unknown address and a wrong password cost the
 * same time and answer byte-identically — is a property of the *server's*
 * answer. `type="email"` is kept because it is the mock's, and because it is
 * what tells a phone to show an email keyboard and a password manager what the
 * field is; `noValidate` stops the browser turning it into a gate. Every
 * attempt reaches the server, and every refusal reads the same.
 */

const PANEL_MAX_WIDTH = 400

const microLabel: CSSProperties = {
  display: 'block',
  fontSize: T.micro.size,
  fontWeight: T.weight.semi,
  letterSpacing: T.micro.tracking,
  textTransform: T.micro.transform,
  color: T.color.onNavy,
}

/** The mock's kickers are a size up from its micro-labels, and tracked wider. */
const kicker: CSSProperties = {
  fontSize: 11,
  fontWeight: T.weight.semi,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
}

const field: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  marginTop: 8,
  padding: '13px 14px',
  border: '2px solid #fff',
  borderRadius: T.radius.square,
  background: '#fff',
  color: T.color.ink,
  fontFamily: T.fontFamily,
  fontSize: 15,
}

const statLabel: CSSProperties = {
  fontSize: T.micro.size,
  fontWeight: T.weight.semi,
  letterSpacing: T.micro.tracking,
  textTransform: T.micro.transform,
  color: T.color.micro,
}

/**
 * The three figures the mock puts under the product statement. They are the
 * seeded program's, not decoration — season `2026-06-01`–`2026-10-15`, window
 * hours 17–22, twelve events of four hours — and they are static copy here
 * rather than fetched, because this screen is the one surface a signed-out
 * visitor sees and it has no authenticated route to read a config from.
 */
const STATS: readonly { label: string; value: string }[] = [
  { label: 'Season', value: '1 Jun – 15 Oct 2026' },
  { label: 'Event window', value: '5:00 – 10:00pm' },
  { label: 'Events assumed', value: '12 · 4 hours' },
]

export interface SignInProps {
  onSignedIn: (user: User) => void
}

export function SignIn({ onSignedIn }: SignInProps): JSX.Element {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    if (submitting) return
    setSubmitting(true)
    // Clear the previous refusal before asking again: leaving it up while a
    // second attempt is in flight shows a rep a verdict on credentials they
    // have already replaced.
    setMessage(null)
    try {
      const result = await api.login(email, password)
      if (result.ok) {
        onSignedIn(result.user)
        return
      }
      // Whatever `api.login` says, verbatim. This screen does not classify
      // failures and has nothing to add: there is exactly one refusal message
      // and it is decided in one place.
      setMessage(result.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: T.fontFamily,
      color: T.color.ink,
    }}>
      <div data-testid="rainbow-rule" style={{ height: T.rainbowHeight, background: T.rainbow }} />
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1.08fr 0.92fr' }}>

        <div style={{
          background: '#fff',
          padding: '56px 64px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}>
          <div>
            <img src={logoUrl} alt="APS" style={{ width: 184, height: 'auto', display: 'block' }} />
            <div style={{ height: 2, background: T.color.rule, margin: '44px 0 34px' }} />
            <div style={{ ...kicker, color: T.color.muted }}>Demand response · commercial</div>
            <h1 style={{
              fontSize: 58,
              lineHeight: 1.02,
              fontWeight: T.weight.bold,
              letterSpacing: '-0.02em',
              margin: '16px 0 0',
              maxWidth: '12ch',
            }}>
              Kilowatt Estimator
            </h1>
            <p style={{
              fontSize: 17,
              lineHeight: 1.5,
              maxWidth: '46ch',
              margin: '20px 0 0',
              color: T.color.body,
            }}>
              How much demand each commercial account could curtail in an event, hour by hour,
              computed from their own interval meter history.
            </p>
          </div>
          <div>
            <div style={{ height: 2, background: T.color.rule, marginBottom: 20 }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24 }}>
              {STATS.map((stat) => (
                <div key={stat.label}>
                  <div style={statLabel}>{stat.label}</div>
                  <div style={{ fontSize: 17, fontWeight: T.weight.semi, marginTop: 7 }}>
                    {stat.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{
          background: T.color.navy,
          color: '#fff',
          padding: '56px 60px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
          <form noValidate onSubmit={(event) => { void submit(event) }} style={{ maxWidth: PANEL_MAX_WIDTH, width: '100%' }}>
            <div style={{ ...kicker, color: T.color.onNavy }}>Authorised users only</div>
            <h2 style={{
              fontSize: 34,
              fontWeight: T.weight.bold,
              letterSpacing: '-0.01em',
              margin: '12px 0 0',
            }}>
              Sign in
            </h2>
            <div style={{ height: 2, background: T.color.cyan, width: 56, margin: '22px 0 30px' }} />

            <label style={{ display: 'block' }}>
              <span style={microLabel}>Work email</span>
              <input
                type="email"
                name="email"
                autoComplete="username"
                placeholder="name@aps.com"
                value={email}
                onChange={(event) => { setEmail(event.target.value) }}
                style={field}
              />
            </label>

            <label style={{ display: 'block', marginTop: 20 }}>
              <span style={microLabel}>Password</span>
              <input
                type="password"
                name="password"
                autoComplete="current-password"
                placeholder="••••••••••"
                value={password}
                onChange={(event) => { setPassword(event.target.value) }}
                style={field}
              />
            </label>

            {message !== null && (
              <div
                role="alert"
                style={{
                  marginTop: 20,
                  border: `2px solid ${T.color.amber}`,
                  background: T.tint.stale,
                  color: T.color.warnInk,
                  padding: '12px 14px',
                  fontSize: 13,
                  lineHeight: 1.45,
                }}
              >
                {message}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              aria-busy={submitting}
              style={{
                marginTop: 24,
                width: '100%',
                textAlign: 'left',
                padding: '15px 18px',
                border: 0,
                borderRadius: T.radius.button,
                background: '#fff',
                color: T.color.navy,
                fontFamily: T.fontFamily,
                fontSize: 14,
                fontWeight: T.weight.bold,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: submitting ? 'progress' : 'pointer',
              }}
            >
              Sign in
            </button>

            <div style={{
              marginTop: 26,
              fontSize: 12,
              lineHeight: 1.55,
              color: T.color.onNavy,
              maxWidth: '38ch',
            }}>
              Access is provisioned by the program administrator. This system holds customer
              meter data.
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
